import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { AlertCircle, Loader2, Plus, Trash2, MapPin } from "lucide-react";
import clientService from "../../services/clientService";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

/**
 * ClientFormModal Component
 * Modal para criar ou editar cliente
 */
const ClientFormModal = ({ isOpen, onClose, onSave, client, isSaving }) => {
  const [formData, setFormData] = useState({
    name: "",
    cpf: "",
    email: "",
    contact: "",
    addresses: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && client) {
      setFormData({
        name: client.name || "",
        cpf: client.cpf || "",
        email: client.email || "",
        contact: client.contact || "",
        addresses: client.addresses || [],
      });
    } else if (isOpen && !client) {
      setFormData({
        name: "",
        cpf: "",
        email: "",
        contact: "",
        addresses: [],
      });
    }
    setErrors({});
  }, [isOpen, client]);

  /**
   * Atualizar campo do formulário
   */
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Aplicar máscara de CPF
   */
  const handleCPFChange = (value) => {
    const cleaned = value.replace(/[^\d]/g, "");
    let formatted = cleaned;

    if (cleaned.length <= 11) {
      if (cleaned.length > 9) {
        formatted = cleaned.replace(
          /(\d{3})(\d{3})(\d{3})(\d{0,2})/,
          "$1.$2.$3-$4"
        );
      } else if (cleaned.length > 6) {
        formatted = cleaned.replace(/(\d{3})(\d{3})(\d{0,3})/, "$1.$2.$3");
      } else if (cleaned.length > 3) {
        formatted = cleaned.replace(/(\d{3})(\d{0,3})/, "$1.$2");
      }
    }

    handleChange("cpf", formatted);
  };

  /**
   * Aplicar máscara de telefone
   */
  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/[^\d]/g, "");
    let formatted = cleaned;

    if (cleaned.length <= 11) {
      if (cleaned.length > 10) {
        formatted = cleaned.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
      } else if (cleaned.length > 6) {
        formatted = cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
      } else if (cleaned.length > 2) {
        formatted = cleaned.replace(/(\d{2})(\d{0,5})/, "($1) $2");
      }
    }

    handleChange("contact", formatted);
  };

  /**
   * Aplicar máscara de CEP
   */
  const handleCEPChange = (value, index) => {
    const cleaned = value.replace(/[^\d]/g, "");
    let formatted = cleaned;

    if (cleaned.length <= 8) {
      if (cleaned.length > 5) {
        formatted = cleaned.replace(/(\d{5})(\d{0,3})/, "$1-$2");
      }
    }

    handleAddressChange(index, "zip_code", formatted);
  };

  /**
   * Adicionar novo endereço
   */
  const addAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addresses: [
        ...prev.addresses,
        {
          zip_code: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city: "",
          state: "",
          reference: "",
        },
      ],
    }));
  };

  /**
   * Remover endereço
   */
  const removeAddress = (index) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index),
    }));
  };

  /**
   * Atualizar campo de endereço
   */
  const handleAddressChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) =>
        i === index ? { ...addr, [field]: value } : addr
      ),
    }));

    const errorKey = `address_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  /**
   * Validar formulário
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.cpf) {
      newErrors.cpf = "CPF é obrigatório";
    } else if (!clientService.validateCPF(formData.cpf)) {
      newErrors.cpf = "CPF inválido";
    }

    if (formData.email && !clientService.validateEmail(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.contact) {
      newErrors.contact = "Telefone é obrigatório";
    } else if (!clientService.validatePhone(formData.contact)) {
      newErrors.contact = "Telefone inválido";
    }

    // Validar endereços
    formData.addresses.forEach((address, index) => {
      if (!address.zip_code) {
        newErrors[`address_${index}_zip_code`] = "CEP é obrigatório";
      }
      if (!address.street) {
        newErrors[`address_${index}_street`] = "Rua é obrigatória";
      }
      if (!address.number) {
        newErrors[`address_${index}_number`] = "Número é obrigatório";
      }
      if (!address.neighborhood) {
        newErrors[`address_${index}_neighborhood`] = "Bairro é obrigatório";
      }
      if (!address.city) {
        newErrors[`address_${index}_city`] = "Cidade é obrigatória";
      }
      if (!address.state) {
        newErrors[`address_${index}_state`] = "Estado é obrigatório";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submeter formulário
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Preparar dados para envio
    const clientData = {
      name: formData.name.trim(),
      cpf: formData.cpf.replace(/[^\d]/g, ""),
      email: formData.email.trim(),
      contact: formData.contact.replace(/[^\d]/g, ""),
      addresses: formData.addresses.map((addr) => ({
        ...(addr.id && { id: addr.id }),
        zip_code: addr.zip_code.replace(/[^\d]/g, ""),
        street: addr.street.trim(),
        number: addr.number.trim(),
        complement: addr.complement?.trim() || "",
        neighborhood: addr.neighborhood.trim(),
        city: addr.city.trim(),
        state: addr.state.trim().toUpperCase(),
        reference: addr.reference?.trim() || "",
      })),
    };

    onSave(clientData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {client ? "Editar Cliente" : "Novo Cliente"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Pessoais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dados Pessoais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nome */}
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Nome Completo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="Ex: João Silva"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* CPF */}
                <div className="space-y-2">
                  <Label htmlFor="cpf">
                    CPF <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="cpf"
                    placeholder="000.000.000-00"
                    value={formData.cpf}
                    onChange={(e) => handleCPFChange(e.target.value)}
                    className={errors.cpf ? "border-red-500" : ""}
                    maxLength={14}
                  />
                  {errors.cpf && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.cpf}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@exemplo.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className={errors.email ? "border-red-500" : ""}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Telefone */}
                <div className="space-y-2">
                  <Label htmlFor="contact">
                    Telefone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact"
                    placeholder="(00) 00000-0000"
                    value={formData.contact}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    className={errors.contact ? "border-red-500" : ""}
                    maxLength={15}
                  />
                  {errors.contact && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.contact}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Endereços */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Endereços
                </CardTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAddress}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Endereço
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.addresses.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Nenhum endereço cadastrado. Clique em "Adicionar Endereço"
                  para começar.
                </p>
              ) : (
                formData.addresses.map((address, index) => (
                  <Card key={index} className="border-2">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">
                          Endereço {index + 1}
                        </CardTitle>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAddress(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* CEP */}
                        <div className="space-y-2">
                          <Label>
                            CEP <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            placeholder="00000-000"
                            value={address.zip_code}
                            onChange={(e) =>
                              handleCEPChange(e.target.value, index)
                            }
                            className={
                              errors[`address_${index}_zip_code`]
                                ? "border-red-500"
                                : ""
                            }
                            maxLength={9}
                          />
                          {errors[`address_${index}_zip_code`] && (
                            <p className="text-sm text-red-500">
                              {errors[`address_${index}_zip_code`]}
                            </p>
                          )}
                        </div>

                        {/* Rua */}
                        <div className="space-y-2 md:col-span-2">
                          <Label>
                            Rua <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            placeholder="Nome da rua"
                            value={address.street}
                            onChange={(e) =>
                              handleAddressChange(
                                index,
                                "street",
                                e.target.value
                              )
                            }
                            className={
                              errors[`address_${index}_street`]
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {errors[`address_${index}_street`] && (
                            <p className="text-sm text-red-500">
                              {errors[`address_${index}_street`]}
                            </p>
                          )}
                        </div>

                        {/* Número */}
                        <div className="space-y-2">
                          <Label>
                            Número <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            placeholder="123"
                            value={address.number}
                            onChange={(e) =>
                              handleAddressChange(
                                index,
                                "number",
                                e.target.value
                              )
                            }
                            className={
                              errors[`address_${index}_number`]
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {errors[`address_${index}_number`] && (
                            <p className="text-sm text-red-500">
                              {errors[`address_${index}_number`]}
                            </p>
                          )}
                        </div>

                        {/* Complemento */}
                        <div className="space-y-2 md:col-span-2">
                          <Label>Complemento</Label>
                          <Input
                            placeholder="Apto, Bloco, etc."
                            value={address.complement}
                            onChange={(e) =>
                              handleAddressChange(
                                index,
                                "complement",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        {/* Bairro */}
                        <div className="space-y-2">
                          <Label>
                            Bairro <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            placeholder="Nome do bairro"
                            value={address.neighborhood}
                            onChange={(e) =>
                              handleAddressChange(
                                index,
                                "neighborhood",
                                e.target.value
                              )
                            }
                            className={
                              errors[`address_${index}_neighborhood`]
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {errors[`address_${index}_neighborhood`] && (
                            <p className="text-sm text-red-500">
                              {errors[`address_${index}_neighborhood`]}
                            </p>
                          )}
                        </div>

                        {/* Cidade */}
                        <div className="space-y-2">
                          <Label>
                            Cidade <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            placeholder="Nome da cidade"
                            value={address.city}
                            onChange={(e) =>
                              handleAddressChange(index, "city", e.target.value)
                            }
                            className={
                              errors[`address_${index}_city`]
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {errors[`address_${index}_city`] && (
                            <p className="text-sm text-red-500">
                              {errors[`address_${index}_city`]}
                            </p>
                          )}
                        </div>

                        {/* Estado */}
                        <div className="space-y-2">
                          <Label>
                            Estado <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            placeholder="SP"
                            value={address.state}
                            onChange={(e) =>
                              handleAddressChange(
                                index,
                                "state",
                                e.target.value
                              )
                            }
                            className={
                              errors[`address_${index}_state`]
                                ? "border-red-500"
                                : ""
                            }
                            maxLength={2}
                          />
                          {errors[`address_${index}_state`] && (
                            <p className="text-sm text-red-500">
                              {errors[`address_${index}_state`]}
                            </p>
                          )}
                        </div>

                        {/* Referência */}
                        <div className="space-y-2 md:col-span-3">
                          <Label>Ponto de Referência</Label>
                          <Input
                            placeholder="Próximo ao metrô, em frente à praça, etc."
                            value={address.reference}
                            onChange={(e) =>
                              handleAddressChange(
                                index,
                                "reference",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : client ? (
                "Atualizar"
              ) : (
                "Criar"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientFormModal;
